import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { IdeaBadges } from '@/components/idea-badges';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useIdeasStore, type Idea } from '@/store/ideas-store';
import { colors, radius, spacing } from '@/theme/tokens';

type Filter = 'ALL' | 'IDEA' | 'BUG' | 'DONE';

const FILTERS: { key: Filter; labelKey: string }[] = [
  { key: 'ALL', labelKey: 'ideas.filterAll' },
  { key: 'IDEA', labelKey: 'ideas.filterIdeas' },
  { key: 'BUG', labelKey: 'ideas.filterBugs' },
  { key: 'DONE', labelKey: 'ideas.filterDone' }, // 4th tab: "Zrealizowane" (owner request)
];

function matches(idea: Idea, f: Filter): boolean {
  if (f === 'ALL') return true;
  if (f === 'DONE') return idea.status === 'DONE';
  return idea.kind === f;
}

/**
 * "Pomysły i błędy" board — mock: tablica_pomysłów. Public wall to submit, vote,
 * and comment; most-voted first so demand ranks our backlog. Reached from Więcej.
 */
export default function IdeasBoardScreen() {
  const { t } = useTranslation();
  const ideas = useIdeasStore((s) => s.ideas);
  const myIdeaIds = useIdeasStore((s) => s.myIdeaIds);
  const [filter, setFilter] = useState<Filter>('ALL');

  const visible = ideas
    .filter((i) => matches(i, filter))
    .sort((a, b) => b.votes - a.votes);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: 96 }}>
        <ScreenHeader title={t('ideas.headerTitle')} />
        <AppText variant="headlineLg">{t('ideas.title')}</AppText>
        <AppText variant="bodyMd">{t('ideas.subtitle')}</AppText>

        {/* Filter chips */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
          {FILTERS.map(({ key, labelKey }) => {
            const active = filter === key;
            return (
              <Pressable
                key={key}
                accessibilityRole="button"
                onPress={() => setFilter(key)}
                style={{
                  backgroundColor: active ? colors.navy : colors.card,
                  borderRadius: radius.full,
                  paddingHorizontal: spacing.md,
                  paddingVertical: spacing.xs,
                }}
              >
                <AppText variant="labelLg" color={active ? '#FFFFFF' : colors.navy}>
                  {t(labelKey)}
                </AppText>
              </Pressable>
            );
          })}
        </View>

        <AppText variant="labelMd" color={colors.primary}>
          {t('ideas.sortMostVoted')}
        </AppText>

        {visible.map((idea) => {
          const done = idea.status === 'DONE';
          return (
            <Pressable
              key={idea.id}
              accessibilityRole="button"
              onPress={() => router.push({ pathname: '/ideas/[id]', params: { id: idea.id } })}
              style={({ pressed }) => ({ transform: [{ scale: pressed ? 0.99 : 1 }] })}
            >
              <Card style={{ flexDirection: 'row', gap: spacing.sm }}>
                {/* Vote count box */}
                <View
                  style={{
                    width: 56,
                    borderRadius: radius.button,
                    backgroundColor: idea.votedByMe ? colors.primaryTile : colors.divider,
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: spacing.sm,
                    gap: 2,
                  }}
                >
                  <Ionicons name="chevron-up" size={18} color={colors.navy} />
                  <AppText variant="headlineSm" color={colors.navy}>
                    {idea.votes}
                  </AppText>
                </View>
                <View style={{ flex: 1, gap: spacing.xs }}>
                  <AppText
                    variant="headlineSm"
                    style={done ? { textDecorationLine: 'line-through', color: colors.outline } : undefined}
                  >
                    {idea.title}
                  </AppText>
                  <AppText variant="bodyMd" numberOfLines={2}>
                    {idea.description}
                  </AppText>
                  <IdeaBadges kind={idea.kind} status={idea.status} mine={myIdeaIds.includes(idea.id)} />
                </View>
              </Card>
            </Pressable>
          );
        })}
      </ScrollView>

      {/* "Zgłoś" FAB */}
      <Pressable
        accessibilityRole="button"
        onPress={() => router.push('/ideas/new')}
        style={({ pressed }) => ({
          position: 'absolute',
          right: spacing.screen,
          bottom: spacing.lg,
          flexDirection: 'row',
          alignItems: 'center',
          gap: spacing.xs,
          backgroundColor: colors.navy,
          borderRadius: radius.full,
          paddingVertical: spacing.sm,
          paddingHorizontal: spacing.lg,
          transform: [{ scale: pressed ? 0.97 : 1 }],
        })}
      >
        <Ionicons name="add" size={22} color="#FFFFFF" />
        <AppText variant="labelLg" color="#FFFFFF">
          {t('ideas.submit')}
        </AppText>
      </Pressable>
    </SafeAreaView>
  );
}
